import { useCallback, useEffect, useRef } from "react";

/**
 * Hook that auto-scales a child text element to the maximum possible font size
 * that still fits inside the container box. Re-runs on resize/orientation change.
 *
 * Usage:
 *   const { containerRef, textRef } = useFitText([deps]);
 *   <div ref={containerRef}><span ref={textRef}>{content}</span></div>
 *
 * The text element should have `white-space: nowrap` and `line-height: 1`
 * for the most accurate measurement.
 */
export default function useFitText(deps = []) {
  const containerRef = useRef(null);
  const textRef = useRef(null);

  const fit = useCallback(() => {
    const box = containerRef.current;
    const text = textRef.current;
    if (!box || !text) return;
    const padding = 8;
    const maxW = box.clientWidth - padding * 2;
    const maxH = box.clientHeight - padding * 2;
    if (maxW <= 20 || maxH <= 20) return;

    text.style.whiteSpace = "nowrap";
    text.style.lineHeight = "1";
    text.style.display = "inline-block";

    // Binary search for the largest font size that fits both dimensions
    let lo = 12;
    let hi = Math.max(maxW, maxH) * 2;
    while (lo + 1 < hi) {
      const mid = (lo + hi) / 2;
      text.style.fontSize = mid + "px";
      if (text.scrollWidth <= maxW && text.scrollHeight <= maxH) {
        lo = mid;
      } else {
        hi = mid;
      }
    }
    text.style.fontSize = Math.floor(lo) + "px";
  }, []);

  // Initial + dep-triggered fit
  useEffect(() => {
    fit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  // Resize / orientation handling
  useEffect(() => {
    fit();
    const onResize = () => fit();
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    let obs = null;
    if (typeof ResizeObserver !== "undefined" && containerRef.current) {
      obs = new ResizeObserver(() => fit());
      obs.observe(containerRef.current);
    }
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
      if (obs) obs.disconnect();
    };
  }, [fit]);

  return { containerRef, textRef, refit: fit };
}
